import { useCallback, useMemo } from "react"
import { Modal } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useWizardContext } from "./WizardContext"
import { getStepConfig, getSteps, getStepFields, NEW_DRAFT_REQUIRED_FIELDS } from "./constants/stepConfig"
import { buildPublishPayload } from "./mappers/toApiPayload"
import { getPublishProblems } from "./helpers/publishRefusal"
import { clearAdjustmentsAfterTicketTypeDeletion } from "./helpers/ticketTypeAdjustments"
import { getSavePayload } from "./helpers/partialDraft"
import { toaster } from "components/common/Toast"
import { getApiErrorMessage } from "helpers/embedded"
import { createTrip, updateTrip } from "store/trips"
import { routes } from "router/routes"

/**
 * Build a stable, comparable string from a step payload for change detection.
 * Payloads are FormData (object-to-formdata), and JSON.stringify(FormData) is
 * always "{}", so we must walk the entries instead — otherwise every save after
 * the first looks "unchanged" and gets skipped. File values are reduced to a
 * name/size/lastModified fingerprint since their contents aren't enumerable.
 */
const payloadToComparable = (payload) => {
  if (!(payload instanceof FormData)) return JSON.stringify(payload ?? null)
  const entries = []
  for (const [key, value] of payload.entries()) {
    entries.push([key, value instanceof File ? `file:${value.name}:${value.size}:${value.lastModified}` : value])
  }
  return JSON.stringify(entries)
}

/**
 * Core wizard logic: validate → save → navigate.
 *
 * Usage: call inside any component rendered within WizardProvider.
 *
 * Steps with non-form local state (scheduling, media, location, resources, digital_waiver)
 * register async save functions in stepSaveHandlersRef. Steps that only use form fields
 * (details, preview) have their handlers registered by the step component directly.
 *
 * Tab navigation is unrestricted — users can jump to any step freely.
 * Clicking a tab saves the current step silently (no validation, no blocking).
 * Next still validates before saving; Save Draft saves a half-filled draft step (see handleSaveDraft).
 */
export const useExperienceWizard = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id: tripId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { form, stepSaveHandlersRef, stepValidateHandlersRef, lastSavedPayloadsRef, isSaving, setIsSaving, completedSteps, markStepCompleted } = useWizardContext()

  const listingType = searchParams.get("type")
  const isEditMode = Boolean(searchParams.get("route"))
  const steps = getSteps(listingType)
  const currentStep = searchParams.get("step") || steps[0]
  const currentIndex = steps.includes(currentStep) ? steps.indexOf(currentStep) : 0

  const stepFields = useMemo(() => getStepFields(listingType), [listingType])

  const showValidationError = useCallback(
    (err) => {
      if (!err?.errorFields?.length) return false
      Modal.error({
        title: t("experiences.validation.incomplete_section"),
        content: (
          <div>
            <p>{t("experiences.validation.missing_fields_message")}</p>
            <ul style={{ marginTop: "8px", paddingLeft: "20px" }}>
              {err.errorFields.map(({ errors }, i) => (
                // index key is safe here — this list is static per render
                // eslint-disable-next-line react/no-array-index-key
                <li key={i}>{errors[0]}</li>
              ))}
            </ul>
            <p style={{ marginTop: "12px" }}>
              {t("experiences.validation.please_complete_required_fields")}
            </p>
          </div>
        ),
        okText: t("global.ok")
      })
      return true
    },
    [t]
  )

  // A refused save (the API's 4xx or 5xx) is not a validation error: say so instead of only logging
  // it, or Next / Save look like they did nothing.
  const showSaveError = useCallback(
    (err, label) => {
      if (showValidationError(err)) return
      console.error(label, err)
      toaster.error(getApiErrorMessage(err) || t("experiences.save_failed"))
    },
    [showValidationError, t]
  )

  const validateStep = useCallback(
    async (stepId) => {
      const fields = stepFields[stepId] || []
      const customHandler = stepValidateHandlersRef.current[stepId]

      // Run form field validation and custom validation in parallel so all errors
      // are collected together — otherwise the first failure stops the second from running.
      const results = await Promise.allSettled([
        fields.length > 0 ? form.validateFields(fields) : Promise.resolve(),
        customHandler ? customHandler() : Promise.resolve()
      ])

      const errors = results
        .filter((r) => r.status === "rejected")
        .flatMap((r) => r.reason?.errorFields || [{ errors: [r.reason?.message || "Validation failed"] }])

      if (errors.length > 0) {
        const err = new Error("Validation failed")
        err.errorFields = errors
        throw err
      }
    },
    [form, stepFields, stepValidateHandlersRef]
  )

  /**
   * Call the step's registered handler to get its serialized payload,
   * then dispatch updateTrip. Returns the API response so callers can
   * read back server-assigned IDs (e.g. ticket_type_id after ticket_type step).
   * Steps without a handler (e.g. preview) are no-ops.
   *
   * Skips the API call if the payload is identical to the last saved snapshot,
   * so navigating between steps without making changes doesn't trigger unnecessary requests.
   */
  const saveStep = useCallback(
    async (stepId, requestOptions, { isPartial = false } = {}) => {
      const handler = stepSaveHandlersRef.current[stepId]
      if (!handler) return null
      const stepPayload = await handler()
      if (!stepPayload) return null
      const payload = getSavePayload(stepPayload, { isPartial })

      const payloadJson = payloadToComparable(payload)

      if (payloadJson === lastSavedPayloadsRef.current[stepId]) return null

      const result = await dispatch(updateTrip(tripId, payload, requestOptions))
      lastSavedPayloadsRef.current[stepId] = payloadJson

      if (stepId === "ticket_type") clearAdjustmentsAfterTicketTypeDeletion(form)

      // After a successful scheduling save, drop schedules flagged _destroy from
      // form state — the backend has removed them, so re-sending the _destroy on a
      // later save would 404 on the now-missing record. Re-snapshot afterwards so
      // the next save compares against the pruned state (no redundant request).
      if (stepId === "scheduling") {
        const schedules = form.getFieldValue("availability_schedules")
        if (Array.isArray(schedules) && schedules.some((schedule) => schedule._destroy)) {
          form.setFieldValue("availability_schedules", schedules.filter((schedule) => !schedule._destroy))
          const prunedPayload = await handler()
          lastSavedPayloadsRef.current[stepId] = payloadToComparable(prunedPayload)
        }
      }

      // Same for start times a duration-type switch deleted (buildTicketTypePayload): forget their
      // ids once saved, so a later save doesn't ask to delete them again.
      if (stepId === "ticket_type" && form.getFieldValue("cleared_slot_ids")?.length) {
        form.setFieldValue("cleared_slot_ids", [])
        const prunedPayload = await handler()
        lastSavedPayloadsRef.current[stepId] = payloadToComparable(prunedPayload)
      }

      // Same for pricing: drop adjustments (and nested date ranges) flagged _destroy
      // after a successful save. The wizard form survives step navigation, so without
      // this the next visit would re-send _destroy for an already-deleted record (422).
      if (stepId === "pricing") {
        const dayAdjustments = form.getFieldValue("dayAdjustments")
        const seasonalAdjustments = form.getFieldValue("seasonalAdjustments")
        let pruned = false

        if (Array.isArray(dayAdjustments) && dayAdjustments.some((adjustment) => adjustment._destroy)) {
          form.setFieldValue("dayAdjustments", dayAdjustments.filter((adjustment) => !adjustment._destroy))
          pruned = true
        }

        const seasonalNeedsPrune =
          Array.isArray(seasonalAdjustments) &&
          seasonalAdjustments.some(
            (adjustment) => adjustment._destroy || (adjustment.dateRanges || []).some((range) => range._destroy)
          )
        if (seasonalNeedsPrune) {
          form.setFieldValue(
            "seasonalAdjustments",
            seasonalAdjustments
              .filter((adjustment) => !adjustment._destroy)
              .map((adjustment) =>
                adjustment.dateRanges
                  ? { ...adjustment, dateRanges: adjustment.dateRanges.filter((range) => !range._destroy) }
                  : adjustment
              )
          )
          pruned = true
        }

        if (pruned) {
          const prunedPayload = await handler()
          lastSavedPayloadsRef.current[stepId] = payloadToComparable(prunedPayload)
        }
      }

      return result
    },
    [stepSaveHandlersRef, lastSavedPayloadsRef, dispatch, tripId, form]
  )

  /**
   * After saving the ticket_type step the API assigns real IDs to each ticket type.
   * Sync those server IDs back into the form so the pricing step can send ticket_type_id.
   */
  const syncTicketTypesFromResponse = useCallback(
    (stepId, result) => {
      if (stepId !== "ticket_type") return
      const tripData = result?.data || result
      if (tripData?.ticket_types?.length) {
        form.setFieldValue("ticket_types", tripData.ticket_types.map(({ max_capacity, ...rest }) => ({ ...rest, maxCapacity: max_capacity })))
        // Rebuild ticketTypePrices keyed by server ID so the Pricing step
        // can send the correct ticket_type_id even for newly created types.
        const currentPrices = form.getFieldValue("ticketTypePrices") || {}
        const updatedPrices = Object.fromEntries(
          tripData.ticket_types.map((ticketType) => [
            ticketType.id,
            currentPrices[ticketType.id] ?? { enabled: ticketType.enabled !== false, price: ticketType.price > 0 ? ticketType.price : null },
          ])
        )
        form.setFieldValue("ticketTypePrices", updatedPrices)
      }
    },
    [form]
  )

  /**
   * For the first save (tripId === "new"): build the payload from the current step's
   * handler, POST it with createTrip, and return the newly assigned trip ID.
   */
  const createAndSave = useCallback(
    async (stepId, { isPartial = false } = {}) => {
      const handler = stepSaveHandlersRef.current[stepId]
      const stepPayload = handler ? await handler() : null
      const payload = getSavePayload(stepPayload, { isPartial })
      const result = await dispatch(createTrip(payload))
      const newId = result?.data?.id || result?.id
      if (!newId) throw new Error("Trip creation failed")
      return newId
    },
    [stepSaveHandlersRef, dispatch]
  )

  const navigateToStep = useCallback(
    (stepId) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set("step", stepId)
        return next
      })
    },
    [setSearchParams]
  )

  const handleNext = useCallback(async () => {
    try {
      setIsSaving(true)
      await validateStep(currentStep)
      markStepCompleted(currentStep)

      if (tripId === "new") {
        const newId = await createAndSave(currentStep)
        const nextIndex = currentIndex + 1
        navigate(
          `${routes.proNewExperienceV2Path({ id: newId })}?type=${listingType}&step=${steps[nextIndex]}`,
          { replace: true }
        )
        return
      }

      const result = await saveStep(currentStep)
      syncTicketTypesFromResponse(currentStep, result)
      if (currentIndex < steps.length - 1) {
        navigateToStep(steps[currentIndex + 1])
      }
    } catch (err) {
      showSaveError(err, "[Wizard] Next step failed:")
    } finally {
      setIsSaving(false)
    }
  }, [currentStep, currentIndex, steps, tripId, listingType, validateStep, saveStep, syncTicketTypesFromResponse, createAndSave, navigateToStep, setIsSaving, showSaveError, navigate, markStepCompleted])

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      navigateToStep(steps[currentIndex - 1])
    }
  }, [currentIndex, steps, navigateToStep])

  const silentSave = useCallback(
    async (stepId) => {
      if (tripId === "new") return
      try { await saveStep(stepId) } catch { /* never block navigation on save failure */ }
    },
    [tripId, saveStep]
  )

  /**
   * Tab click: going back is always allowed (silent save, no validation).
   * Going forward requires the current step to pass validation before saving and navigating.
   * For new trips going forward, createAndSave is called to POST the trip and get a real ID.
   */
  const handleStepClick = useCallback(
    async (stepId) => {
      if (stepId === currentStep) return

      const isGoingBack = steps.indexOf(stepId) < currentIndex

      try {
        setIsSaving(true)
        if (!isGoingBack) {
          await validateStep(currentStep)
          markStepCompleted(currentStep)
        }

        if (tripId === "new" && !isGoingBack) {
          const newId = await createAndSave(currentStep)
          navigate(
            `${routes.proNewExperienceV2Path({ id: newId })}?type=${listingType}&step=${stepId}`,
            { replace: true }
          )
          return
        }

        if (isGoingBack) {
          await silentSave(currentStep)
        } else {
          // Forward tab navigation: non-silent so first-visit changes are actually saved.
          // Errors are swallowed so a save failure never blocks navigation.
          try {
            const result = await saveStep(currentStep)
            syncTicketTypesFromResponse(currentStep, result)
          } catch { /* never block navigation on save failure */ }
        }
        navigateToStep(stepId)
      } catch (err) {
        if (!showValidationError(err)) console.error("[Wizard] Tab navigation failed:", err)
      } finally {
        setIsSaving(false)
      }
    },
    [currentStep, currentIndex, steps, tripId, listingType, validateStep, createAndSave, silentSave, saveStep, syncTicketTypesFromResponse, navigateToStep, setIsSaving, showValidationError, navigate, markStepCompleted]
  )

  /**
   * Save current step without navigating; calls onSuccess on completion.
   *
   * A draft is saved even when the step is half-filled (the step then stays unticked), so the guide
   * can come back to it; a new listing needs its name first. A published or deactivated listing
   * (edit mode) is only saved complete.
   */
  const handleSaveDraft = useCallback(
    async (onSuccess) => {
      try {
        setIsSaving(true)
        let isPartial = false
        try {
          await validateStep(currentStep)
          markStepCompleted(currentStep)
        } catch (err) {
          if (isEditMode) throw err
          if (tripId === "new") await form.validateFields(NEW_DRAFT_REQUIRED_FIELDS)
          isPartial = true
        }

        if (tripId === "new") {
          const newId = await createAndSave(currentStep, { isPartial })
          navigate(
            `${routes.proNewExperienceV2Path({ id: newId })}?type=${listingType}`,
            { replace: true }
          )
          onSuccess?.()
          return
        }

        const result = await saveStep(currentStep, undefined, { isPartial })
        syncTicketTypesFromResponse(currentStep, result)
        onSuccess?.()
      } catch (err) {
        showSaveError(err, "[Wizard] Save draft failed:")
      } finally {
        setIsSaving(false)
      }
    },
    [currentStep, tripId, isEditMode, form, listingType, validateStep, saveStep, syncTicketTypesFromResponse, createAndSave, setIsSaving, showSaveError, navigate, markStepCompleted]
  )

  // A refused publish, in plain words: "Meeting Location: add the meeting point" per missing thing in
  // the Incomplete Section popup, then the first step to fix opens. Without named fields (a server
  // or network error), a short "couldn't publish" message.
  const showPublishRefusal = useCallback(
    (refusal) => {
      const stepConfig = getStepConfig(listingType)
      const problems = getPublishProblems(refusal, steps)
      if (!problems.length) {
        toaster.error(t("experiences.publish_check.failed"))
        return
      }
      const labelOf = (stepId) => stepConfig.find((step) => step.id === stepId)?.label
      showValidationError({
        errorFields: problems.map(({ stepId, messageKey }) => {
          const message = t(`experiences.publish_check.${messageKey}`)
          return { errors: [labelOf(stepId) ? `${labelOf(stepId)}: ${message}` : message] }
        })
      })
      if (problems[0].stepId) navigateToStep(problems[0].stepId)
    },
    [listingType, steps, t, showValidationError, navigateToStep]
  )

  /**
   * Publish the experience (last step action). onRefused runs when the server refuses (e.g. to close
   * the confirm popup); the guide is then told what to fix instead of the server's raw message.
   */
  const handlePublish = useCallback(
    async (onSuccess, onRefused) => {
      try {
        setIsSaving(true)
        // Persist any final edits on the current (preview) step first. Saving wizard_step=preview is
        // what runs every step's server checks, so an unfinished listing is refused here.
        await saveStep(currentStep, { suppressErrorNotification: true })
        // Then publish — mirrors v1: PUT status: "published". Dispatched directly
        // (not via saveStep) so the change-detection dedup guard can't skip it.
        await dispatch(updateTrip(tripId, buildPublishPayload(), { suppressErrorNotification: true }))
        onSuccess?.()
      } catch (err) {
        console.error("[Wizard] Publish failed:", err)
        onRefused?.()
        showPublishRefusal(err)
      } finally {
        setIsSaving(false)
      }
    },
    [currentStep, saveStep, dispatch, tripId, setIsSaving, showPublishRefusal]
  )

  return {
    currentStep,
    currentIndex,
    steps,
    tripId,
    isSaving,
    completedSteps,
    isFirstStep: currentIndex === 0,
    isLastStep: currentIndex === steps.length - 1,
    handleNext,
    handlePrevious,
    handleStepClick,
    handleSaveDraft,
    handlePublish,
    navigateToStep,
    navigate
  }
}
